import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

export default function UseCaseCards() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const useCases = [
    {
      tag: 'Earn → Hold → Borrow',
      title: 'Fee revenue, no sell pressure',
      description: 'Your agent earns platform fees in its native token. Selling creates downward pressure on the token your community holds. Instead — deposit the tokens, borrow ETH, keep earning.',
      visual: 'fees',
    },
    {
      tag: 'Don\'t exit. Leverage.',
      title: 'Hold positions, unlock capital',
      description: 'Your agent is long on $TOKEN. It spots an opportunity that needs ETH. Selling means exiting the position. Borrowing means capturing the opportunity AND keeping the upside.',
      visual: 'portfolio',
    },
    {
      tag: 'Operational liquidity',
      title: 'Pay for compute without selling',
      description: 'AI agents need to pay for inference, storage, bandwidth, cross-chain gas. Borrowing ETH against idle holdings means the agent stays funded without liquidating its treasury.',
      visual: 'compute',
    },
  ];

  return (
    <div ref={ref} className="relative z-10">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-10 sm:mb-12"
      >
        <span 
          className="inline-block text-[11px] uppercase tracking-[0.08em] text-white/50 px-4 py-1.5 rounded-full mb-4"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          Why borrow?
        </span>
        <h3 className="text-[22px] sm:text-[28px] font-semibold text-white px-4">
          Your agent earns tokens. It shouldn't have to dump them.
        </h3>
      </motion.div>

      {/* 3 Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
        {useCases.map((uc, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: i * 0.1 }}
            className="rounded-[20px] overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            {/* Visual Area */}
            <div className="h-[150px] sm:h-[180px] relative overflow-hidden p-6 flex items-center justify-center">
              <div 
                className="absolute inset-0"
                style={{ background: 'radial-gradient(ellipse at 50% 80%, rgba(255,255,255,0.03) 0%, transparent 60%)' }}
              />
              
              {/* Mock Cards - Layered */}
              <div className="relative">
                {/* Back card */}
                <div 
                  className="absolute -top-2 -left-2 w-[120px] sm:w-[140px] h-[70px] sm:h-[80px] rounded-lg transform -rotate-3"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                />
                {/* Front card */}
                <motion.div 
                  className="relative w-[120px] sm:w-[140px] p-3 rounded-lg"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                  animate={{ y: [0, -4, 0] }}
                  transition={{ duration: 3, repeat: Infinity, delay: i * 0.3 }}
                >
                  {uc.visual === 'fees' && (
                    <>
                      <div className="text-[9px] text-white/40 uppercase">Fee Earnings</div>
                      <div className="text-[14px] sm:text-[16px] font-bold text-white mt-1">+2,450 $TOKEN</div>
                      <div className="text-[9px] text-white/30 mt-1">This epoch</div>
                    </>
                  )}
                  {uc.visual === 'portfolio' && (
                    <>
                      <div className="text-[9px] text-white/40 uppercase">Position</div>
                      <div className="text-[14px] sm:text-[16px] font-bold text-white mt-1">$8,200</div>
                      <div className="text-[9px] text-green-400 mt-1">+24.5% unrealized</div>
                    </>
                  )}
                  {uc.visual === 'compute' && (
                    <>
                      <div className="text-[9px] text-white/40 uppercase">Invoice Due</div>
                      <div className="text-[14px] sm:text-[16px] font-bold text-white mt-1">0.8 ETH</div>
                      <div className="text-[9px] text-white/30 mt-1">GPU + API calls</div>
                    </>
                  )}
                </motion.div>
              </div>
            </div>

            {/* Content */}
            <div className="p-5 sm:p-6 pt-0">
              <h4 className="text-[15px] sm:text-[17px] font-semibold text-white mb-2">{uc.title}</h4>
              <p className="text-[12px] sm:text-[13px] text-white/45 leading-relaxed mb-4">{uc.description}</p>
              <span 
                className="inline-block text-[10px] uppercase tracking-wider px-3 py-1 rounded-full"
                style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.6)' }}
              >
                {uc.tag}
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
